import React, { useState } from 'react';
import { useContent } from '../contexts/ContentContext';

const Contact = () => {
  const { getContent } = useContent();
  const pageHeader = getContent('contact.pageHeader') || {};
  const contactInfo = getContent('contact.contactInfo') || [];
  const contactForm = getContent('contact.contactForm') || {};
  const quickActions = getContent('contact.quickActions') || {};
  const trainingLocations = getContent('contact.trainingLocations') || {};
  const registrationLinks = getContent('contact.registrationLinks') || {};
  const faq = getContent('contact.faq') || {};
  const socialNetworks = getContent('contact.socialNetworks') || {};
  const contactCta = getContent('contact.cta') || {};
  
  // State for FAQ accordion
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="contact-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <div className="page-header-content">
            <h1>{pageHeader.title || 'Contactez-nous'}</h1>
            <p>{pageHeader.subtitle || 'Nous sommes toujours disponibles pour répondre à vos questions et vous accompagner dans votre parcours de formation'}</p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="contact-info" id="contact-info">
        <div className="container">
          <div className="contact-grid">
            {contactInfo.map((info, index) => (
              <div key={index} className={`contact-card ${info.type}`}>
                <h3>{info.title}</h3>
                <p>{info.description}</p>
                {info.link ? (
                  <a href={info.link} className="contact-link" target={info.link.includes('wa.me') ? '_blank' : '_self'} rel={info.link.includes('wa.me') ? 'noopener noreferrer' : undefined}>
                    {info.value}
                  </a>
                ) : (
                  <span className="contact-link">{info.value}</span>
                )}
                <span className="availability">{info.availability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact-form-section" id="contact-form">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>{contactForm.title || 'Demander des Informations'}</h2>
              <p>{contactForm.subtitle || 'Remplissez ce formulaire et nous vous contacterons rapidement pour discuter de vos besoins de formation'}</p>
            </div>
            
            <form className="contact-form" id="contactForm">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nom complet *</label>
                  <input type="text" id="name" name="name" required />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input type="email" id="email" name="email" required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Téléphone *</label>
                  <input type="tel" id="phone" name="phone" required />
                </div>
                
                <div className="form-group">
                  <label htmlFor="city">Ville</label>
                  <select id="city" name="city">
                    <option value="">Sélectionner votre ville</option>
                    {(contactForm.cities || []).map((city, index) => (
                      <option key={index} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="program">Formation d'intérêt</label>
                <select id="program" name="program">
                  <option value="">Sélectionner une formation</option>
                  {(contactForm.subjects || []).map((subject, index) => (
                    <option key={index} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="experience">Années d'expérience</label>
                <select id="experience" name="experience">
                  <option value="">Sélectionner votre expérience</option>
                  {(contactForm.experienceOptions || []).map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea id="message" name="message" rows="5" placeholder={contactForm.messagePlaceholder || "Décrivez vos besoins spécifiques, questions ou demandes particulières..."}></textarea>
              </div>
              
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" id="newsletter" name="newsletter" />
                  <span className="checkmark"></span>
                  {contactForm.newsletterText || 'Je souhaite recevoir les actualités et offres spéciales de DentoXcellence'}
                </label>
              </div>
              
              <button type="submit" className="btn btn-primary submit-btn">
                <i className="fas fa-paper-plane"></i>
                {contactForm.submitText || 'Envoyer la Demande'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="quick-actions" id="quick-actions">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{quickActions.title || 'Actions Rapides'}</h2>
            <p className="section-subtitle">
              {quickActions.subtitle || 'Choisissez la méthode de contact qui vous convient le mieux'}
            </p>
          </div>
          
          <div className="actions-grid">
            {(quickActions.actions || []).map((action, index) => (
              <div key={index} className={`action-card ${action.type}`}>
              <div className="action-icon">
                  <i className={action.icon}></i>
              </div>
                <h3>{action.title}</h3>
                <p>{action.description}</p>
                <a href={action.link} className={action.buttonClass} target={action.link.includes('wa.me') ? '_blank' : '_self'} rel={action.link.includes('wa.me') ? 'noopener noreferrer' : undefined}>
                  <i className={action.buttonIcon}></i>
                  {action.buttonText}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Locations */}
      <section className="training-locations" id="locations">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{trainingLocations.title || 'Lieux de Formation'}</h2>
            <p className="section-subtitle">
              {trainingLocations.subtitle || 'Nos formations se déroulent dans les principales villes d\'Algérie'}
            </p>
          </div>
          
          <div className="locations-grid">
            {(trainingLocations.locations || []).map((location, index) => (
              <div key={index} className={`location-card ${location.type}`}>
              <div className="location-icon">
                <i className="fas fa-building"></i>
              </div>
                <h3>{location.city}</h3>
                <p className="location-type">{location.description}</p>
                <p>{location.address}</p>
              <div className="location-features">
                  {(location.features || []).map((feature, featureIndex) => (
                    <span key={featureIndex} className="feature">• {feature}</span>
                  ))}
            </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Links */}
      <section className="registration-section" id="registration">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{registrationLinks.title || 'Liens d\'Inscription Rapide'}</h2>
            <p className="section-subtitle">
              {registrationLinks.subtitle || 'Choisissez votre formation et inscrivez-vous immédiatement'}
            </p>
          </div>

          <div className="registration-grid">
            {(registrationLinks.programs || []).map((program, index) => (
              <div key={index} className={`registration-card ${program.popular ? 'popular' : ''}`}>
                <div className="registration-header">
                  <h3>{program.name}</h3>
                  {program.popular && <span className="popular-badge">Populaire</span>}
                </div>
                <p>{program.description}</p>
                <div className="program-details">
                  <div className="detail">
                    <i className="fas fa-clock"></i>
                    <span>{program.duration}</span>
              </div>
                  <div className="detail">
                    <i className="fas fa-tag"></i>
                    <span>{program.price}</span>
            </div>
              </div>
                <a href="/formations#registration-form" className="btn btn-primary">
                <i className="fas fa-user-plus"></i>
                  S'inscrire
              </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{faq.title || 'Questions Fréquentes'}</h2>
            <p className="section-subtitle">
              {faq.subtitle || 'Trouvez rapidement les réponses à vos questions les plus courantes'}
            </p>
          </div>
          
          <div className="faq-container">
            {(faq.questions || []).map((item, index) => (
              <div key={index} className={`faq-item ${openFaqIndex === index ? 'active' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(index)}>
                  <h3>{item.question}</h3>
                  <i className={`fas fa-chevron-${openFaqIndex === index ? 'up' : 'down'}`}></i>
                </div>
                <div className="faq-answer" style={{ display: openFaqIndex === index ? 'block' : 'none' }}>
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Networks */}
      <section className="social-networks">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{socialNetworks.title || 'Suivez-nous sur les Réseaux Sociaux'}</h2>
            <p className="section-subtitle">
              {socialNetworks.subtitle || 'Restez informé de nos dernières actualités et formations'}
            </p>
          </div>
          
          <div className="social-grid">
            {(socialNetworks.networks || []).map((network, index) => (
              <div key={index} className={`social-card ${network.name.toLowerCase()}`}>
              <div className="social-icon">
                  <i className={network.icon}></i>
              </div>
                <h3>{network.name}</h3>
                <p>{network.description}</p>
                <a href={network.url} className="btn btn-social" target="_blank" rel="noopener noreferrer">
                  <i className={network.icon}></i>
                Suivre
              </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>{contactCta.title || 'Prêt à Transformer Votre Pratique ?'}</h2>
            <p>{contactCta.subtitle || 'Rejoignez plus de 500 dentistes qui ont déjà fait confiance à DentoXcellence'}</p>
            <div className="cta-buttons">
              {(contactCta.buttons || []).map((button, index) => (
                <a key={index} href={button.link} className={`btn btn-${button.type}`} target={button.target} rel={button.target === '_blank' ? 'noopener noreferrer' : undefined}>
                  <i className={button.icon}></i>
                  {button.text}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;